import * as Xstate from "@xstate/react";
import React, { FC, useState } from "react";
import { GlobalContext } from "../../app/stateMachines/GlobalContext";
import Button from "../../components/button/Button";
import { OutlineButton } from "../../components/button/OutlineButton";
import { useAppDispatch, useAppSelector } from "../../redux/redux";
import { startTimer, timerSlice } from "../../services/TimerService";
import { SelectOptionType } from "../../types/types";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../../slices/ContactsSlice";
import { useDebounce } from "usehooks-ts";
import CustomSelect from "../../components/select/Select";
import { getInitialsFromFullName } from "../../constants/constants";
import { useGetSearchCustomersWorksQuery } from "../../slices/WorkSlice";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { startTimerProblem } from "./startTimer.utils";

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  defaultContact?: SelectOptionType;
  /** Ask ProCharted "start a timer on …": the work item starts picked. */
  defaultWork?: SelectOptionType;
  /** Called after Start (the timer request was sent). */
  onStarted?: () => void;
};

const StartTimer: FC<Props> = ({
  isOpen,
  closeModal,
  defaultContact,
  defaultWork,
  onStarted,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.appReducer);

  const [work, setWork] = useState<SelectOptionType | null>(
    defaultWork ?? null,
  );
  const [searchContacts, setSearchContacts] = useState("");
  const [contact, setContact] = useState<SelectOptionType | null>(
    defaultWork ? defaultContact ?? null : null,
  );

  const searchContactsDebounce = useDebounce(searchContacts, 50);

  const { data: works, isFetching: isLoadingWorks } =
    useGetSearchCustomersWorksQuery(
      {
        customerId: contact?.value || "",
        orgId: getLinkedFirm()?.orgId || "",
      },
      { skip: !contact?.value },
    );

  const { data: contacts, isFetching: isLoadingContacts } =
    useGetSearchCustomersQuery({
      searchQuery: searchContactsDebounce || "ab",
      orgId: getLinkedFirm()?.orgId || "",
      userId: user.id,
      isActive: true,
    });

  const contactsOptions = (): SelectOptionType[] => {
    const options: SelectOptionType[] = [];
    if (contacts) {
      contacts.forEach((contact) => {
        options.push({
          value: contact?.customerId,
          label: `${contact.customerName}`,
          avatar: getInitialsFromFullName(contact?.customerName),
          avatarColor: contact.businessContactId ? "purple" : "blue",
        });
      });
    }

    return options;
  };
  // Roadmap 1805 (owner 2026-10-21): Start stays grey until a work item is picked.
  const startProblem = startTimerProblem(contact, work);

  const onSubmit = async () => {
    if (!contact || startProblem) {
      return;
    }
    if (contact.value) {
      dispatch(
        startTimer({
          workId: work?.value || null,
          customerId: contact.value || "",
          assignedUserId: user.id,
          customerName: contact.label,
          work: work
            ? {
                id: work.value,
                name: work.label,
              }
            : null,
        }),
      );
      if (onStarted) onStarted();
      else closeModal();
    }
  };

  return (
    <div
      onClick={closeModal}
      id={"my-popup"}
      className={
        "fixed left-0 top-0 z-[4] flex h-screen w-screen items-center justify-center bg-gray-500/20"
      }>
      <div
        className={
          "max-h-[750px] w-[480px] overflow-hidden rounded-2xl bg-white p-6 "
        }
        onClick={(event) => event.stopPropagation()}>
        <div>
          <div className={""}>
            <div className={"mb-10 w-full text-center text-[18px] font-bold"}>
              Start Timer
            </div>
            <div className={"flex h-[350px] flex-col justify-between "}>
              <div>
                <div className={"relative z-[1] mb-4"}>
                  <div className={"label-text mb-2"}>Contact</div>
                  <div>
                    <CustomSelect
                      options={contactsOptions()}
                      value={contact}
                      onChange={(newValue) => {
                        const newContact = newValue as SelectOptionType;
                        if (newContact?.value !== contact?.value) {
                          setWork(null); // work items belong to the client
                        }
                        setContact(newContact);
                      }}
                      inputValue={searchContacts}
                      onChangeInput={setSearchContacts}
                      withAvatar
                      withMenu
                      isLoading={isLoadingContacts}
                      selectStyle={{ fullWidth: true }}
                    />
                  </div>
                </div>
                <div className={"mb-4"}>
                  <div className={"label-text mb-2"}>Work</div>
                  <CustomSelect
                    options={works || []}
                    value={work}
                    onChange={(newValue) => {
                      const value = newValue as SelectOptionType;
                      setWork(value);
                    }}
                    withMenu
                    isLoading={isLoadingWorks}
                    placeholder={"Search..."}
                    selectStyle={{ fullWidth: true }}
                  />
                </div>
              </div>
              <div className={"flex justify-end"}>
                <div className={"flex space-x-[10px]"}>
                  <span
                    data-tooltip-id={"start-timer-needs-work"}
                    data-tooltip-content={startProblem || ""}>
                    <Button
                      buttonType={"button"}
                      label={"Start"}
                      onClick={onSubmit}
                      disabled={!!startProblem}
                      extraClasses={"normal-case"}
                    />
                  </span>
                  {startProblem && (
                    <ReactTooltip
                      id={"start-timer-needs-work"}
                      place={"top"}
                      className={"z-10 !rounded-[8px] !p-2 !text-sm"}
                    />
                  )}
                  <OutlineButton
                    buttonType={"button"}
                    label={"Cancel"}
                    colorType={"neutral"}
                    onClick={closeModal}
                    extraClasses={"normal-case"}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartTimer;
